import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import SellerLayout from './SellerLayout';

const TINYMCE_SCRIPT_ID = 'seller-tinymce-script';

const SellerProductAdd = () => {
  const [loading, setLoading] = useState(true);
  const [shopName, setShopName] = useState('');
  const [categories, setCategories] = useState([]);
  const [previewImage, setPreviewImage] = useState('');
  const [subPreviewImages, setSubPreviewImages] = useState([]);
  const [editorReady, setEditorReady] = useState(false);

  const [form, setForm] = useState({
    name: '',
    category_id: '',
    short_description: '',
    description: '',
    price: '',
    stock: '',
  });

  const imageRef = useRef(null);
  const subImagesRef = useRef(null);
  const descriptionRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get('/api/seller/product/add-data', {
          withCredentials: true,
        });

        if (res.data && res.data.ok) {
          setShopName(res.data.shop_name || '');
          setCategories(Array.isArray(res.data.categories) ? res.data.categories : []);
        } else {
          alert(res.data?.error || '데이터를 불러오지 못했습니다.');
        }
      } catch (err) {
        const redirect = err?.response?.data?.redirect;
        const error = err?.response?.data?.error;

        if (redirect) {
          window.location.href = redirect;
          return;
        }

        alert(error || '데이터를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (loading) return;

    const initEditor = () => {
      if (!window.tinymce || !descriptionRef.current) return;

      const existing = window.tinymce.get('description');
      if (existing) {
        existing.remove();
      }

      window.tinymce.init({
        selector: '#description',
        height: 400,
        plugins: 'image link media table code lists',
        toolbar:
          'undo redo | styleselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist | image media link | code',
        language: 'ko_KR',
        images_upload_url: '/api/seller/upload/detail-image',
        images_upload_credentials: true,
        automatic_uploads: true,
        images_reuse_filename: true,
        relative_urls: true,
        remove_script_host: true,
        document_base_url: '/',
        setup: (editor) => {
          editor.on('init', () => {
            setEditorReady(true);
            editor.setContent(form.description || '');
          });

          editor.on('change keyup undo redo', () => {
            const content = editor.getContent();
            setForm((prev) => ({
              ...prev,
              description: content,
            }));
          });
        },
      });
    };

    if (window.tinymce) {
      initEditor();
      return;
    }

    let script = document.getElementById(TINYMCE_SCRIPT_ID);
    if (!script) {
      script = document.createElement('script');
      script.id = TINYMCE_SCRIPT_ID;
      script.src = '/static/flask_js/tinymce/tinymce.min.js';
      script.async = true;
      script.onload = () => {
        initEditor();
      };
      document.body.appendChild(script);
    } else {
      script.onload = () => {
        initEditor();
      };
      if (window.tinymce) {
        initEditor();
      }
    }

    return () => {
      if (window.tinymce) {
        const editor = window.tinymce.get('description');
        if (editor) {
          editor.remove();
        }
      }
    };
  }, [loading]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMainImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setPreviewImage('');
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      setPreviewImage(ev.target?.result || '');
    };
    reader.readAsDataURL(file);
  };

  const handleSubImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    const previews = [];

    if (files.length === 0) {
      setSubPreviewImages([]);
      return;
    }

    let done = 0;
    files.forEach((file, idx) => {
      if (!file.type.startsWith('image/')) {
        done += 1;
        if (done === files.length) {
          setSubPreviewImages(previews.filter(Boolean));
        }
        return;
      }

      const reader = new FileReader();
      reader.onload = (ev) => {
        previews[idx] = ev.target?.result || '';
        done += 1;
        if (done === files.length) {
          setSubPreviewImages(previews.filter(Boolean));
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const resizeImage = (file, maxWidth) => {
    return new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target.result;
      };

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = height * (maxWidth / width);
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          if (!blob) {
            resolve(file);
            return;
          }

          const resizedFile = new File([blob], file.name, {
            type: file.type || 'image/jpeg',
            lastModified: Date.now(),
          });
          resolve(resizedFile);
        }, file.type || 'image/jpeg', 0.8);
      };

      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (window.tinymce) {
      window.tinymce.triggerSave();
      const editor = window.tinymce.get('description');
      if (editor) {
        form.description = editor.getContent();
      }
    }

    const formData = new FormData();

    const mainFile = imageRef.current?.files?.[0];
    if (mainFile) {
      const resizedMainImage = await resizeImage(mainFile, 1200);
      formData.append('image', resizedMainImage);
    }

    const subFiles = Array.from(subImagesRef.current?.files || []);
    if (subFiles.length > 0) {
      const resizedSubImages = await Promise.all(
        subFiles.map((file) => resizeImage(file, 1200))
      );
      resizedSubImages.forEach((file) => {
        formData.append('sub_images[]', file);
      });
    }

    formData.append('name', form.name);
    formData.append('category_id', form.category_id);
    formData.append('short_description', form.short_description);
    formData.append('description', form.description);
    formData.append('price', form.price);
    formData.append('stock', form.stock);

    try {
      const res = await fetch('/api/seller/product/add-process', {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
      });

      const text = await res.text();
      const trimmed = (text || '').trim();

      if (
        trimmed.startsWith('/') ||
        trimmed.startsWith('http') ||
        trimmed.endsWith('.php')
      ) {
        window.location.href = trimmed;
      } else {
        alert(`오류: ${trimmed}`);
      }
    } catch (err) {
      alert('전송 중 오류가 발생했습니다.');
    }
  };

  if (loading) {
    return (
      <SellerLayout shopName={shopName}>
        <div style={styles.loadingWrap}>로딩중...</div>
      </SellerLayout>
    );
  }

  return (
    <SellerLayout shopName={shopName}>
      <h2 style={styles.title}>상품 등록</h2>

      <form onSubmit={handleSubmit} style={styles.formCard}>
        <div style={styles.mb3}>
          <label style={styles.label}>상품명</label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            style={styles.input}
            required
          />
        </div>

        <div style={styles.mb3}>
          <label style={styles.label}>카테고리</label>
          <select
            name="category_id"
            value={form.category_id}
            onChange={handleChange}
            style={styles.select}
          >
            <option value="">선택하세요</option>
            {categories.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.mb3}>
          <label style={styles.label}>간단설명</label>
          <input
            type="text"
            name="short_description"
            value={form.short_description}
            onChange={handleChange}
            style={styles.input}
            required
          />
        </div>

        <div style={styles.mb3}>
          <label style={styles.label}>상세설명</label>
          <textarea
            id="description"
            ref={descriptionRef}
            name="description"
            rows="10"
            defaultValue={form.description}
            style={styles.textarea}
          />
          {!editorReady ? (
            <div style={styles.editorLoading}>에디터 로딩중...</div>
          ) : null}
        </div>

        <div style={styles.mb3}>
          <label style={styles.label}>가격(원)</label>
          <input
            type="number"
            name="price"
            value={form.price}
            onChange={handleChange}
            style={styles.input}
            required
          />
        </div>

        <div style={styles.mb3}>
          <label style={styles.label}>재고 수량</label>
          <input
            type="number"
            name="stock"
            value={form.stock}
            onChange={handleChange}
            style={styles.input}
            required
          />
        </div>

        <div style={styles.mb3}>
          <label style={styles.label}>대표 이미지</label>
          {previewImage ? (
            <img src={previewImage} alt="" style={styles.mainPreview} />
          ) : null}
          <input
            type="file"
            ref={imageRef}
            name="image"
            onChange={handleMainImageChange}
            style={styles.input}
            accept="image/*"
          />
        </div>

        <div style={styles.mb3}>
          <label style={styles.label}>추가 이미지 (여러 개 선택 가능)</label>
          <div style={styles.subPreviewWrap}>
            {subPreviewImages.map((src, idx) => (
              <img key={idx} src={src} alt="" style={styles.subPreview} />
            ))}
          </div>
          <input
            type="file"
            ref={subImagesRef}
            name="sub_images[]"
            multiple
            onChange={handleSubImagesChange}
            style={styles.input}
            accept="image/*"
          />
        </div>

        <div style={styles.formFooter}>
          <button type="submit" style={styles.submitButton}>
            상품 등록
          </button>
        </div>
      </form>
    </SellerLayout>
  );
};

const styles = {
  loadingWrap: {
    padding: '40px 0',
  },
  title: {
    marginTop: '20px',
    marginBottom: '30px',
    fontWeight: 'bold',
    borderBottom: '2px solid #dee2e6',
    paddingBottom: '10px',
  },
  formCard: {
    padding: '24px',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid #dee2e6',
  },
  mb3: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontWeight: 600,
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: '6px',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: '6px',
    boxSizing: 'border-box',
    background: '#fff',
  },
  textarea: {
    width: '100%',
    minHeight: '220px',
    padding: '10px 12px',
    border: '1px solid #ced4da',
    borderRadius: '6px',
    boxSizing: 'border-box',
    resize: 'vertical',
  },
  editorLoading: {
    marginTop: '8px',
    fontSize: '12px',
    color: '#666',
  },
  mainPreview: {
    maxWidth: '200px',
    display: 'block',
    marginBottom: '10px',
  },
  subPreviewWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginTop: '10px',
    marginBottom: '10px',
  },
  subPreview: {
    maxWidth: '150px',
    maxHeight: '150px',
    border: '1px solid #ddd',
    padding: '4px',
    borderRadius: '4px',
  },
  formFooter: {
    textAlign: 'right',
  },
  submitButton: {
    background: '#0d6efd',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 16px',
    cursor: 'pointer',
  },
};

export default SellerProductAdd;